import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { Input } from '@/components/shadcn/input';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/shadcn/sheet';
import type { MabarParticipant } from '@/services/communityService';
import type { PadelGameListItem } from '@/services/padelService';
import padelService from '@/services/padelService';

interface PadelRoundLineupEditorProps {
  isOpen: boolean;
  onClose: () => void;
  game: PadelGameListItem;
  participants: MabarParticipant[];
  isSparring?: boolean;
  teamAName?: string;
  teamBName?: string;
  onSuccess: () => void;
  onError: (message: string) => void;
}

type SlotKey = { side: 'a' | 'b'; index: number };

export default function PadelRoundLineupEditor({
  isOpen,
  onClose,
  game,
  participants,
  isSparring = false,
  teamAName = 'Tim A',
  teamBName = 'Tim B',
  onSuccess,
  onError,
}: PadelRoundLineupEditorProps) {
  const [sideA, setSideA] = useState<number[]>([]);
  const [sideB, setSideB] = useState<number[]>([]);
  const [picker, setPicker] = useState<SlotKey | null>(null);
  const [query, setQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const confirmed = useMemo(
    () => participants.filter((p) => p.status === 'confirmed'),
    [participants]
  );

  const lineupIds = useMemo(() => new Set([...sideA, ...sideB].filter(Boolean)), [sideA, sideB]);

  useEffect(() => {
    if (!isOpen) return;
    const a = game.sideAPlayers.map((p) => p.participantId);
    while (a.length < 2) a.push(0);
    const b = game.sideBPlayers.map((p) => p.participantId);
    while (b.length < 2) b.push(0);
    setSideA(a);
    setSideB(b);
    setPicker(null);
    setQuery('');
  }, [isOpen, game]);

  const isTeamBPlayer = (p: MabarParticipant) => {
    if (!p.team) return false;
    const t = p.team.trim().toLowerCase();
    const bName = teamBName ? teamBName.trim().toLowerCase() : '';
    return t === 'team_b' || t === 'tim b' || (bName !== '' && t === bName);
  };

  const teamAParticipants = useMemo(() => {
    if (!isSparring) return confirmed;
    return confirmed.filter((p) => !isTeamBPlayer(p) || sideA.includes(p.id));
  }, [confirmed, isSparring, teamBName, sideA]);

  const teamBParticipants = useMemo(() => {
    if (!isSparring) return confirmed;
    return confirmed.filter((p) => isTeamBPlayer(p) || sideB.includes(p.id));
  }, [confirmed, isSparring, teamBName, sideB]);

  const replaceSlot = (slot: SlotKey, participantId: number) => {
    if (slot.side === 'a') {
      const next = [...sideA];
      next[slot.index] = participantId;
      setSideA(next);
    } else {
      const next = [...sideB];
      next[slot.index] = participantId;
      setSideB(next);
    }
    setPicker(null);
    setQuery('');
  };

  const candidates = useMemo(() => {
    const list = !isSparring
      ? confirmed
      : picker?.side === 'a'
        ? teamAParticipants
        : teamBParticipants;

    return list.filter((p) => {
      const currentSlotId = picker ? (picker.side === 'a' ? sideA[picker.index] : sideB[picker.index]) : 0;
      if (lineupIds.has(p.id) && currentSlotId !== p.id) {
        return false;
      }
      if (query && !p.name.toLowerCase().includes(query.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [confirmed, isSparring, picker, teamAParticipants, teamBParticipants, lineupIds, sideA, sideB, query]);

  const isRoundGame = game.roundNumber != null && game.sessionSlug != null;

  const handleSave = async () => {
    const a = sideA.filter((n) => n > 0);
    const b = sideB.filter((n) => n > 0);
    if (a.length !== 2 || b.length !== 2) {
      onError('Lengkapi susunan pemain (2v2)');
      return;
    }
    setSubmitting(true);
    try {
      if (isRoundGame) {
        const used = new Set([...a, ...b]);
        const queue = confirmed.map((p) => p.id).filter((id) => !used.has(id));
        const payload: { sideA: number[]; sideB: number[]; queue: number[]; courtNumber?: number } = {
          sideA: a,
          sideB: b,
          queue,
        };
        if (game.courtNumber != null) payload.courtNumber = game.courtNumber;
        await padelService.updateRoundLineup(game.sessionSlug, game.roundNumber as number, payload);
      } else {
        await padelService.updateGameLineup(game.slug, { sideA: a, sideB: b });
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      onError(msg || (err instanceof Error ? err.message : 'Gagal menyimpan susunan'));
    } finally {
      setSubmitting(false);
    }
  };

  const getPlayerName = (participantId: number, slotIndex: number) => {
    if (!participantId) return `Pemain ${slotIndex + 1} (Kosong)`;
    return (
      participants.find((p) => p.id === participantId)?.name ||
      [...(game?.sideAPlayers ?? []), ...(game?.sideBPlayers ?? [])].find(
        (p) => p.participantId === participantId
      )?.playerName ||
      `Pemain #${participantId}`
    );
  };

  const renderRow = (participantId: number, sideLabel: string, slot: SlotKey) => {
    const name = getPlayerName(participantId, slot.index);
    return (
      <Card
        key={`${slot.side}-${slot.index}`}
        className="flex flex-row items-center justify-between rounded-2xl border-gray-100 px-3.5 py-3 shadow-none"
      >
        <div>
          <p className="text-sm font-extrabold text-[#0E1726]">{name}</p>
          <p className="text-xs text-gray-400">{sideLabel}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          className="h-auto gap-1 px-0 text-sm font-semibold text-[#FC480D] hover:bg-transparent hover:text-[#D83B08]"
          onClick={() => setPicker(slot)}
        >
          <Icon icon="mdi:swap-horizontal" className="h-4 w-4" />
          {participantId ? 'Ganti' : 'Pilih'}
        </Button>
      </Card>
    );
  };

  const labelA = isSparring ? teamAName : 'Tim A';
  const labelB = isSparring ? teamBName : 'Tim B';
  const gameTitle =
    game.roundNumber != null
      ? game.courtNumber != null
        ? `Round ${game.roundNumber} · Lap. ${game.courtNumber}`
        : `Round ${game.roundNumber}`
      : '';

  return (
    <>
      <Sheet
        open={isOpen && picker == null}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
      >
        <SheetContent side="bottom" className="rounded-t-3xl">
          <SheetHeader>
            <SheetTitle>Edit Player {gameTitle ? `· ${gameTitle}` : ''}</SheetTitle>
            <SheetDescription>Pilih pemain yang mau diganti.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {sideA.map((id, index) => renderRow(id, labelA, { side: 'a', index }))}
            {sideB.map((id, index) => renderRow(id, labelB, { side: 'b', index }))}
          </div>
          <Button
            type="button"
            disabled={submitting}
            className="mt-5 h-12 w-full rounded-full bg-[#FC480D] font-bold text-white hover:bg-[#D83B08]"
            onClick={handleSave}
          >
            {submitting ? 'Menyimpan...' : 'Simpan Perubahan'}
          </Button>
        </SheetContent>
      </Sheet>

      <Sheet open={picker != null} onOpenChange={(next) => !next && setPicker(null)}>
        <SheetContent side="bottom" className="rounded-t-3xl">
          <SheetHeader>
            <SheetTitle>Pilih Pengganti</SheetTitle>
            <SheetDescription>
              {picker?.side === 'a' ? labelA : labelB}
            </SheetDescription>
          </SheetHeader>
          <div className="relative mt-3">
            <Icon icon="mdi:magnify" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari nama pemain"
              className="h-11 rounded-full pl-9"
            />
          </div>
          <div className="mt-3 max-h-80 space-y-2 overflow-y-auto">
            {candidates.map((player) => (
              <Button
                key={player.id}
                type="button"
                variant="outline"
                className="h-auto w-full justify-between rounded-2xl border-gray-100 px-3.5 py-3 hover:border-[#FC480D] hover:bg-[#FFF8F5]"
                onClick={() => picker && replaceSlot(picker, player.id)}
              >
                <span className="text-left">
                  <span className="block text-sm font-extrabold text-[#0E1726]">{player.name}</span>
                  {player.team && (
                    <span className="block text-xs font-normal text-gray-400">{player.team}</span>
                  )}
                </span>
                <Icon icon="mdi:plus-circle-outline" className="h-5 w-5 text-[#FC480D]" />
              </Button>
            ))}
            {candidates.length === 0 && (
              <p className="py-6 text-center text-xs text-gray-400">Tidak ada pemain yang cocok</p>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
